import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { HistoricalMovementsBuilder } from '../../models/builders/historical-movements.builder';
import { ToolManagementValidationBuilder } from '../../models/builders/tool-management-validation.builder';
import { ToolManagamentBuilder } from '../../models/builders/tool-managament.builder';
// tslint:disable-next-line: max-line-length
import { RequestOrderInterface } from '../../../dashboard/tool-management/request-order/request-order/request-order-list/request-order.interface';
// tslint:disable-next-line: max-line-length
import { SendValidationInterface } from '../../../dashboard/tool-management/check-reconstruction/check-reconstruction/check-reconstruction-list/send-validation.interface';
import { HistoricalMovementTypesBuilder } from '../../models/builders/historical-movement-types.builder';
import { isNullOrUndefined } from 'util';
import { PendingOrdersBuilder } from '../../models/builders/pending-orders.builder';
import { PendingOrderTypesBuilder } from '../../models/builders/pending-order-type.builder';

const checkTooBody = (
  hash: string,
  originalRebuild: number,
  originalStore: number,
  rebuild: number,
  scrap: number,
  store: number,
  isToolHolderPublic: boolean
) => ({
  hash: hash,
  originalRebuild: originalRebuild,
  rebuild: rebuild,
  scrap: scrap,
  originalStore: originalStore,
  store: store,
  isHolder: isToolHolderPublic
});

const getRebuildBody = (data: {
  hash: string,
  toValidateCounter: number,
  toScrappedCounter: number,
  toRebuildCounter: number,
  option: string,
  isHolder: boolean
}) => (
  {
  hash: data.hash,
  toValidateCounter: data.toValidateCounter,
  toScrappedCounter: data.toScrappedCounter,
  toRebuildCounter: data.toRebuildCounter,
  optionHash: data.option,
  isHolder: data.isHolder
});

const getRebuildsBody = (data: {
  hash: string,
  toValidateCounter: number,
  toScrappedCounter: number,
  toRebuildCounter: number,
  option: string,
  isHolder: boolean
}[]) => ({
  rebuilds: data.map(d => getRebuildBody(d))
});

const getValidationBody = (data: SendValidationInterface) => ({
  hash: data.hash,
  toERPCounter: data.toERPCounter,
  delete: data.delete,
  isRebuild: data.isRebuild,
  validationQuantity: data.validationQuantity
});

const getRequestOrederBody = (data: RequestOrderInterface) => ({
  toolHash: data.toolHash,
  isHolder: data.isHolder.toString(),
  requestQuantity: data.requestQuantity
});

const getValidationsBody = (data: SendValidationInterface[]) => ({
  validations: data.map(d => getValidationBody(d))
});

const getRequestOredersBody = (requestOrders: RequestOrderInterface[]) => ({
  requestOrders: requestOrders.map(d => getRequestOrederBody(d))
});

@Injectable()
export class ToolManagementApiService {
  private apiUrl: string;

  constructor(private httpClient: HttpClient) {
    this.apiUrl = `${environment.apiUrl}tool-managements`;
  }

  searchFilteredToolManagements(isHolder: boolean, category: string, toolTemplate: string, search: string, hasErp: Boolean) {
    let params = new HttpParams();
    if (isHolder !== null && isHolder !== undefined) {
      params = params.set('isHolder', isHolder.toString());
    } if (category) {
      params = params.set('category', category);
    } if (toolTemplate) {
      params = params.set('toolTemplate', toolTemplate);
    } if (search) {
      params = params.set('search', search);
    } if (!isNullOrUndefined(hasErp)) {
      params = params.set('hasErp', hasErp.toString());
    }

    return this.httpClient.get(`${this.apiUrl}/search-tools`, { params }).pipe(
      map(ToolManagamentBuilder.fromList));
  }

  getToolManagement(hash: string, isHolder: boolean) {
    const params = new HttpParams()
      .set('isHolder', isHolder.toString());

    return this.httpClient.get(`${this.apiUrl}/tools/${hash}`, { params }).pipe(map(ToolManagamentBuilder.fromJson));
  }

  getFilteredPendingOrders(
    name: string,
    isHolder: boolean,
    isTool: boolean,
    hasOrder: boolean,
    hasValidation: boolean,
    categoryHash: string,
    sort: string,
    isReversed: boolean,
    page: number,
    size: number
  ) {
    let params = new HttpParams()
      .set('name', name ? name : '')
      .set('isHolder', isHolder.toString())
      .set('isTool', isTool.toString())
      .set('hasOrder', hasOrder.toString())
      .set('hasValidation', hasValidation.toString())
      .set('sort', sort)
      .set('isReversed', (!isReversed).toString())
      .set('page', page.toString())
      .set('size', size.toString());
    if (!isNullOrUndefined(categoryHash)) {
      params = params.set('categoryHash', categoryHash);
    }

    return this.httpClient.get(`${this.apiUrl}/pending-orders`, { params }).pipe(map(PendingOrdersBuilder.fromJson));
  }

  getFilteredHistoricalMovements(
    name: string,
    categoryHash: string,
    isHolder: boolean,
    isTool: boolean,
    type: number,
    startDate: number,
    endDate: number,
    userHash: string,
    sort: string,
    isReversed: boolean,
    page: number,
    size: number
  ) {
    let params = new HttpParams()
      .set('name', name ? name : '')
      .set('isHolder', isHolder.toString())
      .set('isTool', isTool.toString())
      .set('sort', sort)
      .set('isReversed', (!isReversed).toString())
      .set('page', page.toString())
      .set('size', size.toString());
    if (!isNullOrUndefined(categoryHash)) {
      params = params.set('categoryHash', categoryHash);
    }
    if (type != null) {
      params = params.set('type', type.toString());
    }
    if (!isNullOrUndefined(userHash) && userHash !== '') {
      params = params.set('userHash', userHash.toString());
    }
    if (startDate != null && endDate != null) {
      params = params.set('startDate', startDate.toString());
      params = params.set('endDate', endDate.toString());
    }

    return this.httpClient.get(`${this.apiUrl}/historical-movements`, { params }).pipe(map(HistoricalMovementsBuilder.fromJson));
  }

  getHistoricalMovementTypes() {
    return this.httpClient.get(`${this.apiUrl}/historical-movement-types`).pipe(map(HistoricalMovementTypesBuilder.fromList));
  }

  getPendingOrderTypes() {
    return this.httpClient.get(`${this.apiUrl}/pending-order-types`).pipe(map(PendingOrderTypesBuilder.fromList));
  }

  checkTool(
    hash: string,
    originalRebuild: number,
    originalToStore: number,
    rebuild: number,
    scrap: number,
    store: number,
    isToolHolderPublic: boolean
  ) {
    return this.httpClient.put(
      `${this.apiUrl}/${hash}`,
      checkTooBody(hash, originalRebuild, originalToStore, rebuild, scrap, store, isToolHolderPublic)
    );
  }

  searchToolManagementWithToRebuildCounter(search: string) {
    let params = new HttpParams();
    if (search) {
      params = params.set('search', search);
    }

    return this.httpClient.get(`${this.apiUrl}/to-rebuild`, { params }).pipe(map(ToolManagamentBuilder.fromList));
  }

  toolManagementApiService(data: {
    hash: string,
    toValidateCounter: number,
    toScrappedCounter: number,
    toRebuildCounter: number,
    option: string,
    isHolder: boolean
  }[]) {
    return this.httpClient.put(`${this.apiUrl}/rebuild`, getRebuildsBody(data)).pipe(map(ToolManagamentBuilder.fromList));
  }

  searchToolManagementValidationsToSend(search: string) {
    let params = new HttpParams();
    if (search) {
      params = params.set('search', search);
    }

    return this.httpClient.get(
      `${this.apiUrl}/validations-to-send`,
       { params }
      ).pipe(map(ToolManagementValidationBuilder.fromList));
  }

  validateReconstruction(data: SendValidationInterface[]) {
    const body = getValidationsBody(data);
    return this.httpClient.post(`${this.apiUrl}/send-validations`, body);
  }

  requestOrders(requestOrders: RequestOrderInterface[]) {
    const body = getRequestOredersBody(requestOrders);
    return this.httpClient.post(`${this.apiUrl}/request-orders`, body);
  }

}



// WEBPACK FOOTER //
// /var/www/borneo/prod/borneo_app/src/app/core/services/api/tool-api-management.service.ts